// Top-level router with session gate.
//
// The session gate logic lives here so every route is auto-protected:
// - ready: render the actual routes inside the consumer / embed shell
// - exchanging / idle: render a spinner so the user sees activity
// - no-shopify: render the magic-link Login page (the public beta path)
// - error: render an error banner with a retry button

import { Frame, Spinner } from '@shopify/polaris';
import type { ReactNode } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { BarcodeCapture } from './components/BarcodeCapture';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PhotoCapture } from './components/PhotoCapture';
import { SeniorButton } from './components/senior/SeniorButton';
import { SeniorCard } from './components/senior/SeniorCard';
import { useSession } from './hooks/SessionProvider';
import { EmbedProvider, useIsEmbedded } from './hooks/useIsEmbedded';
import { AppPage } from './layouts/AppPage';
import { ConsumerShell } from './layouts/ConsumerShell';
import { Account } from './pages/Account';
import { AddByName } from './pages/AddByName';
import { AdvisoryBoard } from './pages/AdvisoryBoard';
import { Allergies } from './pages/Allergies';
import { CarePlan } from './pages/CarePlan';
import { Chat } from './pages/Chat';
import { ComparePlans } from './pages/ComparePlans';
import { Connect } from './pages/Connect';
import { Doctors } from './pages/Doctors';
import { Family } from './pages/Family';
import { Food } from './pages/Food';
import { Home } from './pages/Home';
import { Interactions } from './pages/Interactions';
import { Login } from './pages/Login';
import { Medications } from './pages/Medications';
import { Methodology } from './pages/Methodology';
import { NotFound } from './pages/NotFound';
import { PillIdentify } from './pages/PillIdentify';
import { Pillbox } from './pages/Pillbox';
import { Refills } from './pages/Refills';
import { Reminders } from './pages/Reminders';
import { Settings } from './pages/Settings';
import { Shopping } from './pages/Shopping';
import { Symptoms } from './pages/Symptoms';
import { Videos } from './pages/Videos';
import { Welcome } from './pages/Welcome';

export function App() {
  return (
    <ErrorBoundary>
      <EmbedProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </EmbedProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { status, errorMessage, refresh } = useSession();
  const isEmbedded = useIsEmbedded();

  if (status === 'idle' || status === 'exchanging') {
    return (
      <ChromeFrame embedded={isEmbedded}>
        <AppPage title="Signing you in">
          <SeniorCard>
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Spinner size="large" />
              <p style={{ marginTop: 16, fontSize: 18 }}>Signing you in...</p>
            </div>
          </SeniorCard>
        </AppPage>
      </ChromeFrame>
    );
  }

  if (status === 'no-shopify') {
    return <Login />;
  }

  if (status === 'error') {
    return (
      <ChromeFrame embedded={isEmbedded}>
        <AppPage title="Sign-in problem">
          <SeniorCard title="Could not sign you in">
            <p style={{ fontSize: 18, lineHeight: 1.6 }}>
              {errorMessage ??
                'Something went wrong signing you in. Tap the button below to try again.'}
            </p>
            <SeniorButton
              variant="primary"
              onClick={() => {
                void refresh();
              }}
              fullWidth
            >
              Try again
            </SeniorButton>
          </SeniorCard>
        </AppPage>
      </ChromeFrame>
    );
  }

  // status === 'ready'
  return (
    <ChromeFrame embedded={isEmbedded}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/medications" element={<Medications />} />
        <Route path="/medications/add-by-photo" element={<PhotoCapture />} />
        <Route path="/medications/add-by-barcode" element={<BarcodeCapture />} />
        <Route path="/medications/add-by-name" element={<AddByName />} />
        <Route path="/doctors" element={<Doctors />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/food" element={<Food />} />
        <Route path="/pill-identify" element={<PillIdentify />} />
        <Route path="/interactions" element={<Interactions />} />
        <Route path="/pillbox" element={<Pillbox />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/symptoms" element={<Symptoms />} />
        <Route path="/refills" element={<Refills />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/allergies" element={<Allergies />} />
        <Route path="/family" element={<Family />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/care-plan" element={<CarePlan />} />
        <Route path="/account" element={<Account />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/compare-plans" element={<ComparePlans />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/advisory-board" element={<AdvisoryBoard />} />
        <Route path="/shopping" element={<Shopping />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ChromeFrame>
  );
}

function ChromeFrame({
  embedded,
  children,
}: {
  embedded: boolean;
  children: ReactNode;
}): JSX.Element {
  if (embedded) {
    return <Frame>{children}</Frame>;
  }
  return <ConsumerShell>{children}</ConsumerShell>;
}
